import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Btn, Alert, StatusBadge, StandardModal, colors, inputStyle, selectStyle, textareaStyle } from "../design-system";


const API = "/api";
const token = () => localStorage.getItem("token");
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

// ---------------------------------------------------------------------------
// Decision type configuration
// ---------------------------------------------------------------------------
const DECISIONS = {
  start_review:        { label: "Start Review",           color: "#0ea5e9", icon: "🔍", description: "Begin formal ICSC review of this application." },
  approve:             { label: "Approve",                color: "#22c55e", icon: "✓",  description: "Grant full membership. Membership number is assigned and invoice raised." },
  conditional_approve: { label: "Conditional Approval",   color: "#f59e0b", icon: "⚠",  description: "Grant provisional membership subject to listed conditions." },
  reject:              { label: "Reject",                 color: "#ef4444", icon: "✗",  description: "Refuse the application. The applicant may re-apply after addressing gaps." },
  suspend:             { label: "Suspend",                color: "#f97316", icon: "⏸",  description: "Temporarily suspend membership pending investigation." },
  revoke:              { label: "Revoke",                 color: "#dc2626", icon: "⊘",  description: "Permanently revoke membership. Membership number cannot be reused." },
  reinstate:           { label: "Reinstate",              color: "#22c55e", icon: "↺",  description: "Reinstate a suspended membership to active status." },
  condition_resolved:  { label: "Resolve Condition(s)",   color: "#22c55e", icon: "☑",  description: "Mark one or more conditions as satisfied. Membership auto-activates when all conditions are cleared." },
  renew:               { label: "Renew",                  color: "#6366f1", icon: "♻",  description: "Renew membership for a further period." },
  note:                { label: "Add Note",               color: "#94a3b8", icon: "📝", description: "Record a note on this membership without changing its status." },
};

const ALLOWED_FROM = {
  draft:        ["note"],
  submitted:    ["start_review", "note"],
  under_review: ["approve", "conditional_approve", "reject", "note"],
  conditional:  ["approve", "reject", "condition_resolved", "revoke", "note"],
  approved:     [],
  rejected:     ["note"],
  active:       ["suspend", "revoke", "renew", "note"],
  suspended:    ["reinstate", "revoke", "note"],
  lapsed:       ["renew", "note"],
  expired:      ["renew", "note"],
  revoked:      ["note"],
};

// ---------------------------------------------------------------------------
// Timeline entry
// ---------------------------------------------------------------------------
function TimelineEntry({ entry, index, isLast }) {
  const conf  = DECISIONS[entry.decision_type] || { color: "#94a3b8", icon: "•", label: entry.decision_type };
  const color = conf.color;
  return (
    <div style={{ display: "flex", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: color + "20", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color, fontWeight: 700 }}>
          {conf.icon}
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, background: colors.border, marginTop: 4, minHeight: 20 }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color }}>{conf.label}</span>
          <span style={{ fontSize: 11, color: colors.textMuted }}>
            {new Date(entry.decided_at).toLocaleString("en-GB")}
          </span>
        </div>
        {entry.previous_status && entry.new_status && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <StatusBadge status={entry.previous_status} size="sm" />
            <span style={{ color: colors.textMuted, fontSize: 12 }}>→</span>
            <StatusBadge status={entry.new_status} size="sm" />
          </div>
        )}
        <p style={{ fontSize: 13, margin: "0 0 6px", color: colors.textSecondary, lineHeight: 1.5 }}>{entry.rationale}</p>
        {entry.conditions?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
            {entry.conditions.map((c, i) => (
              <span key={i} style={{ fontSize: 11, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "2px 8px", color: colors.warning }}>
                {c}
              </span>
            ))}
          </div>
        )}
        <span style={{ fontSize: 11, color: colors.textMuted }}>by {entry.decided_by}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MembershipDecisionPanel() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [app, setApp]           = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState(null);
  const [msgType, setMsgType]   = useState("success");

  // Decision modal
  const [decModal, setDecModal]     = useState(false);
  const [decType, setDecType]       = useState(null);
  const [rationale, setRationale]   = useState("");
  const [conditions, setConditions] = useState([]);
  const [newCond, setNewCond]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Resolve conditions modal
  const [resolveModal, setResolveModal]   = useState(false);
  const [resolveChecked, setResolveChecked] = useState([]);
  const [resolveNote, setResolveNote]     = useState("");

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(async () => {
    try {
      const [aRes, dRes] = await Promise.all([
        fetch(`${API}/memberships/applications/${id}`,             { headers: authHeaders() }),
        fetch(`${API}/memberships/applications/${id}/decision-log`, { headers: authHeaders() }),
      ]);
      if (aRes.ok) setApp(await aRes.json());
      if (dRes.ok) setDecisions(await dRes.json());
    } catch {
      flash("Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function openDecision(type) {
    setDecType(type);
    setRationale("");
    setConditions([]);
    setNewCond("");
    setDecModal(true);
  }

  function openResolve() {
    setResolveChecked([]);
    setResolveNote("");
    setResolveModal(true);
  }

  async function submitDecision(e) {
    e.preventDefault();
    if (!rationale.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        decision_type: decType,
        rationale: rationale.trim(),
        conditions: conditions.length ? conditions : null,
      };
      const res = await fetch(`${API}/memberships/applications/${id}/decision`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Decision failed");
      flash(`Decision recorded: ${DECISIONS[decType]?.label}`);
      setDecModal(false);
      load();
    } catch (e) {
      flash(e.message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitResolve(e) {
    e.preventDefault();
    if (!resolveChecked.length) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/memberships/applications/${id}/decision`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          decision_type: "condition_resolved",
          rationale: resolveNote || "Conditions satisfied and resolved.",
          conditions: resolveChecked,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      flash("Conditions resolved");
      setResolveModal(false);
      load();
    } catch (e) {
      flash(e.message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading…</div>;
  if (!app)    return <div style={{ padding: 40, color: colors.danger }}>Application not found.</div>;

  const availableDecisions = (ALLOWED_FROM[app.status] || []).filter(k => k !== "condition_resolved");
  const currentConditions  = app.conditions || [];
  const conf = DECISIONS;

  // Requirements guidance per decision type
  const REQUIREMENTS = {
    approve:             "All 6 evidence items should be accepted before approving.",
    conditional_approve: "List each outstanding condition the applicant must satisfy.",
    reject:              "Provide a clear rationale referencing the evidence gaps.",
    suspend:             "State the grounds and expected duration of suspension.",
    revoke:              "State the confirmed malpractice or breach. This action is permanent.",
    reinstate:           "Confirm the grounds for suspension have been resolved.",
    renew:               "Confirm renewal fee has been invoiced or received.",
    note:                "Use notes for correspondence, reminders, and contextual records.",
  };

  return (
    <div style={{ padding: 32, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Btn variant="ghost" size="sm" onClick={() => navigate(`/membership/applications/${id}`)}>← Application</Btn>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Decision Panel</h1>
        </div>
      </div>

      {/* Member summary strip */}
      <div style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "12px 20px", marginBottom: 24, display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Applicant</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{app.applicant_name}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Grade</div>
          <div style={{ fontWeight: 600 }}>{app.grade_name} ({app.post_nominal})</div>
        </div>
        {app.membership_number && (
          <div>
            <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Number</div>
            <div style={{ fontFamily: "monospace", fontWeight: 700, color: colors.primary }}>{app.membership_number}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Status</div>
          <StatusBadge status={app.status} />
        </div>
        {app.valid_until && (
          <div>
            <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Expires</div>
            <div style={{ fontWeight: 600 }}>{new Date(app.valid_until).toLocaleDateString("en-GB")}</div>
          </div>
        )}
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}>
        {/* LEFT: Timeline */}
        <Card padding={28}>
          <h3 style={{ fontWeight: 700, marginBottom: 24, fontSize: 16 }}>Decision History</h3>
          {decisions.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: "center", padding: 40 }}>No decisions recorded yet.</p>
          ) : decisions.map((d, i) => (
            <TimelineEntry key={d.id} entry={d} index={i} isLast={i === decisions.length - 1} />
          ))}
        </Card>

        {/* RIGHT: Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Outstanding conditions */}
          {currentConditions.length > 0 && (
            <Card padding={20} accent={colors.warning}>
              <h3 style={{ fontWeight: 700, fontSize: 14, color: colors.warning, marginBottom: 12 }}>Outstanding Conditions</h3>
              {currentConditions.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 13 }}>
                  <span style={{ color: colors.warning }}>○</span>
                  <span>{c}</span>
                </div>
              ))}
              <Btn variant="success" size="sm" style={{ marginTop: 12, width: "100%" }} onClick={openResolve}>
                Resolve Conditions
              </Btn>
            </Card>
          )}

          {/* Available decisions */}
          <Card padding={20}>
            <h3 style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Available Actions</h3>
            {availableDecisions.length === 0 ? (
              <p style={{ color: colors.textMuted, fontSize: 13 }}>No actions available for current status.</p>
            ) : availableDecisions.map(key => {
              const c = conf[key];
              return (
                <button
                  key={key}
                  onClick={() => openDecision(key)}
                  style={{
                    width: "100%", padding: "10px 14px", marginBottom: 8, border: `1px solid ${c.color}44`,
                    borderRadius: 8, background: c.color + "0d", cursor: "pointer", textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10, transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = c.color + "22"}
                  onMouseLeave={e => e.currentTarget.style.background = c.color + "0d"}
                >
                  <span style={{ fontSize: 18 }}>{c.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: c.color }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>{c.description}</div>
                  </div>
                </button>
              );
            })}
          </Card>

          {/* State machine reference */}
          <Card padding={20}>
            <h3 style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: colors.textSecondary }}>Status Reference</h3>
            {Object.entries(ALLOWED_FROM).map(([status, acts]) => (
              <div key={status} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12, borderBottom: `1px solid ${colors.border}`, color: status === app.status ? colors.primary : colors.textSecondary, fontWeight: status === app.status ? 700 : 400 }}>
                <span style={{ textTransform: "capitalize" }}>{status}</span>
                <span style={{ color: colors.textMuted }}>{acts.length} action{acts.length !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {/* ---- Decision Modal ---- */}
      {decModal && decType && (
        <StandardModal
          title={`${conf[decType]?.icon} ${conf[decType]?.label}`}
          onClose={() => setDecModal(false)}
          width={520}
          danger={["reject", "revoke"].includes(decType)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setDecModal(false)}>Cancel</Btn>
              <Btn
                variant={["reject","revoke","suspend"].includes(decType) ? "danger" : "primary"}
                type="submit"
                form="decision-form"
                disabled={submitting}
              >
                {submitting ? "Recording…" : `Confirm: ${conf[decType]?.label}`}
              </Btn>
            </div>
          }
        >
          <div style={{ padding: "10px 14px", background: conf[decType]?.color + "14", borderRadius: 8, marginBottom: 16, fontSize: 13, color: colors.textSecondary }}>
            {conf[decType]?.description}
          </div>
          {REQUIREMENTS[decType] && (
            <div style={{ padding: "8px 12px", background: colors.background, borderRadius: 6, marginBottom: 14, fontSize: 12, color: colors.info, border: `1px solid ${colors.border}` }}>
              {REQUIREMENTS[decType]}
            </div>
          )}
          <form id="decision-form" onSubmit={submitDecision}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>
                Rationale <span aria-hidden="true" style={{ color: colors.danger }}>*</span>
              </label>
              <textarea
                required
                rows={4}
                value={rationale}
                onChange={e => setRationale(e.target.value)}
                placeholder={decType === "note" ? "Enter note text…" : "Provide clear rationale for this decision…"}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
              />
            </div>

            {decType === "conditional_approve" && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Conditions</label>
                {conditions.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                    <span style={{ flex: 1, padding: "6px 10px", background: colors.background, border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, color: colors.warning }}>{c}</span>
                    <Btn size="sm" variant="danger" onClick={() => setConditions(conditions.filter((_, j) => j !== i))}>×</Btn>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={newCond}
                    onChange={e => setNewCond(e.target.value)}
                    placeholder="Add a condition…"
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (newCond.trim()) { setConditions([...conditions, newCond.trim()]); setNewCond(""); } } }}
                    style={{ flex: 1, padding: "7px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13 }}
                  />
                  <Btn size="sm" variant="outline" onClick={() => { if (newCond.trim()) { setConditions([...conditions, newCond.trim()]); setNewCond(""); } }}>Add</Btn>
                </div>
              </div>
            )}
          </form>
        </StandardModal>
      )}

      {/* ---- Resolve Conditions Modal ---- */}
      {resolveModal && (
        <StandardModal
          title="Resolve Conditions"
          onClose={() => setResolveModal(false)}
          width={480}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setResolveModal(false)}>Cancel</Btn>
              <Btn variant="success" type="submit" form="resolve-form" disabled={submitting || !resolveChecked.length}>
                {submitting ? "Resolving…" : "Mark Resolved"}
              </Btn>
            </div>
          }
        >
          <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}>
            Select the conditions that have been satisfied. Membership will automatically activate when all conditions are cleared.
          </p>
          <form id="resolve-form" onSubmit={submitResolve}>
            <div style={{ marginBottom: 14 }}>
              {currentConditions.map((c, i) => (
                <label key={i} style={{ display: "flex", gap: 10, padding: "8px 10px", marginBottom: 6, borderRadius: 6, background: colors.background, border: `1px solid ${colors.border}`, cursor: "pointer", alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    checked={resolveChecked.includes(c)}
                    onChange={e => {
                      if (e.target.checked) setResolveChecked([...resolveChecked, c]);
                      else setResolveChecked(resolveChecked.filter(x => x !== c));
                    }}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13 }}>{c}</span>
                </label>
              ))}
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Resolution Notes</label>
              <textarea
                rows={3}
                value={resolveNote}
                onChange={e => setResolveNote(e.target.value)}
                placeholder="Briefly describe how each condition was satisfied…"
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
          </form>
          {resolveChecked.length === currentConditions.length && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: colors.background, borderRadius: 6, fontSize: 13, color: colors.success, border: `1px solid ${colors.border}` }}>
              All conditions selected — membership will be activated automatically.
            </div>
          )}
        </StandardModal>
      )}
    </div>
  );
}
